import api from "./axios";
import { type User, mapUserResponse } from "../types/user";
import type { UiDensity } from "../constants/density";
import axios from "axios";
import { store } from "../store";
import { isErrorResponse } from "../utils/errorHandling";
import i18n from "../translations/i18n";

// Create a special instance for password reset that doesn't handle auth errors
const passwordResetApi = axios.create({
  baseURL: api.defaults.baseURL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add the same request interceptor as the main api instance
passwordResetApi.interceptors.request.use(
  (config) => {
    const token = store.getState().user.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Set Accept-Language header based on selected language
    const language = i18n.language || localStorage.getItem("language") || "id";
    config.headers["Accept-Language"] = language;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Don't add auth interceptors to this instance
passwordResetApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only handle non-401 errors
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }
    return Promise.reject(error);
  },
);

export interface UserResponse {
  data: {
    user: User;
  };
}

export interface GetCurrentUserResponse {
  user: User;
}

export const getCurrentUserApi = async (): Promise<GetCurrentUserResponse> => {
  try {
    const response = await api.get<any>("/api/v1/users/whoami");
    return {
      user: mapUserResponse(response.data.data.user),
    };
  } catch (error) {
    console.error("Error fetching current user:", error);
    throw error;
  }
};

export interface UsersResponse {
  data: {
    users: User[];
  };
}

export interface CreateUserData {
  _id: string;
  name: string;
  role: string;
  password: string;
}

export const getUsersApi = async (): Promise<User[]> => {
  try {
    const response = await api.get<UsersResponse>("/api/v1/users");
    return response.data.data.users.map((user) => mapUserResponse(user));
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

export const createUserApi = async (userData: CreateUserData): Promise<void> => {
  try {
    await api.post("/api/v1/users", userData);
  } catch (error) {
    console.error("Error creating user:", error);
    throw error;
  }
};

export const resetUserPasswordApi = async (userId: string, newPassword: string): Promise<void> => {
  try {
    await api.post(`/api/v1/users/${userId}/adminResetPassword`, {
      _id: userId,
      newPassword,
    });
  } catch (error) {
    console.error("Error resetting user password:", error);
    throw error;
  }
};

export const updateUserRoleApi = async (userId: string, role: string): Promise<User> => {
  try {
    const response = await api.patch<{ data: { user: User } }>(`/api/v1/users/${userId}`, {
      role,
    });
    return mapUserResponse(response.data.data.user);
  } catch (error) {
    console.error("Error updating user role:", error);
    throw error;
  }
};

export interface LoginResponse {
  data: {
    token: string;
    user: User;
  };
}

export const loginApi = async (formData: {
  id: string;
  password: string;
  timezone?: string;
}): Promise<LoginResponse> => {
  try {
    const response = await api.post<LoginResponse>("/api/v1/users/login", formData);
    return {
      data: {
        token: response.data.data.token,
        user: mapUserResponse(response.data.data.user),
      },
    };
  } catch (error: any) {
    if (isErrorResponse(error)) {
      throw error;
    }
    throw error;
  }
};

export interface RefreshTokenResponse {
  data: {
    token: string;
    user: User;
    refreshToken?: string;
  };
}

export const refreshTokenApi = async (): Promise<RefreshTokenResponse> => {
  try {
    const response = await api.post<any>("/api/v1/users/refresh");
    return {
      data: {
        token: response.data.data.token,
        user: mapUserResponse(response.data.data.user),
        refreshToken: response.data.data.refreshToken,
      },
    };
  } catch (error) {
    console.error("Error refreshing token:", error);
    throw error;
  }
};

export const logoutApi = async (): Promise<void> => {
  try {
    await api.post("/api/v1/users/logout");
  } catch (error) {
    console.error("Error logging out:", error);
    throw error;
  }
};

export interface ResetPasswordData {
  _id: string;
  password: string;
  newPassword: string;
}

export class WrongPasswordError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "WrongPasswordError";
  }
}

export const resetPasswordApi = async (userId: string, data: ResetPasswordData): Promise<void> => {
  try {
    await passwordResetApi.post(`/api/v1/users/${userId}/resetPassword`, {
      ...data,
      _id: userId,
    });
  } catch (error: any) {
    if (error.response?.status === 401 && error.response?.data?.message?.includes("password")) {
      throw new WrongPasswordError(error.response.data.message);
    }
    throw error;
  }
};

export interface UpdateMyProfileData {
  name?: string;
  timezone?: string;
  timezoneManual?: boolean;
  uiDensityDesktop?: UiDensity;
  uiDensityMobile?: UiDensity;
}

export const updateMyProfileApi = async (data: UpdateMyProfileData): Promise<User> => {
  const response = await api.patch<{ data: { user: User } }>("/api/v1/users/me", data);
  return mapUserResponse(response.data.data.user);
};

export const getUserProfileApi = async (userId: string): Promise<User> => {
  const response = await api.get<{ data: { user: User } }>(`/api/v1/users/${userId}/profile`);
  return mapUserResponse(response.data.data.user);
};

export const updateUserProfileApi = async (
  userId: string,
  data: UpdateMyProfileData,
): Promise<User> => {
  const response = await api.patch<{ data: { user: User } }>(
    `/api/v1/users/${userId}/profile`,
    data,
  );
  return mapUserResponse(response.data.data.user);
};

export interface ProfilePictureUploadUrlResponse {
  uploadUrl: string;
  key: string;
  maxBytes: number;
}

export const getProfilePictureUploadUrlApi = async (
  contentType: string,
  userId?: string,
): Promise<ProfilePictureUploadUrlResponse> => {
  const path = userId
    ? `/api/v1/users/${userId}/profile-picture/upload-url`
    : "/api/v1/users/me/profile-picture/upload-url";
  const response = await api.post<{ data: ProfilePictureUploadUrlResponse }>(path, {
    contentType,
  });
  return response.data.data;
};

export const confirmProfilePictureApi = async (key: string, userId?: string): Promise<User> => {
  const path = userId
    ? `/api/v1/users/${userId}/profile-picture/confirm`
    : "/api/v1/users/me/profile-picture/confirm";
  const response = await api.post<{ data: { user: User } }>(path, { key });
  return mapUserResponse(response.data.data.user);
};

export const deleteProfilePictureApi = async (userId?: string): Promise<User> => {
  const path = userId
    ? `/api/v1/users/${userId}/profile-picture`
    : "/api/v1/users/me/profile-picture";
  const response = await api.delete<{ data: { user: User } }>(path);
  return mapUserResponse(response.data.data.user);
};

export const uploadProfilePictureFile = async (file: File, userId?: string): Promise<User> => {
  const maxBytes = 2 * 1024 * 1024;
  if (file.size > maxBytes) {
    throw new Error("PROFILE_PICTURE_TOO_LARGE");
  }

  const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
  if (!allowedTypes.includes(file.type)) {
    throw new Error("PROFILE_PICTURE_INVALID_TYPE");
  }

  const { uploadUrl, key } = await getProfilePictureUploadUrlApi(file.type, userId);
  const putResponse = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  });

  if (!putResponse.ok) {
    throw new Error("PROFILE_PICTURE_UPLOAD_FAILED");
  }

  return confirmProfilePictureApi(key, userId);
};
